import { useEffect, useRef, useState } from 'react'

import {
  EMPTY_LIST,
  EMPTY_OBJECT,
  ENTITY_KEY_ID,
} from '../../../../constants/common'
import {
  EVENT_KEY_ARROW_DOWN,
  EVENT_KEY_ARROW_UP,
  EVENT_NAME_KEY_DOWN,
} from '../../../../constants/event'
import { isValueFunctionType } from '../../../../utils/common'
import { preventDefault } from '../../../../utils/events'

import { INITIAL_INDEX } from './useTableArrowNavigation.constants'

export function useTableArrowNavigation({
  containerRef,
  disabled,
  focusedRow,
  initialIndex = INITIAL_INDEX,
  isItemDisabled,
  itemIdKey = ENTITY_KEY_ID,
  items = EMPTY_LIST,
  scrollIntoView = true,
  onActiveIndexChange,
} = EMPTY_OBJECT) {
  const [activeIndex, setActiveIndex] = useState(() =>
    focusedRow
      ? items.findIndex(item => item[itemIdKey] === focusedRow)
      : initialIndex
  )
  const [prevFocusedRow, setPrevFocusedRow] = useState(focusedRow)

  const activeIndexRef = useRef(activeIndex)

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  // Sync the active index with external `focusedRow` — during render (initial
  // value derived above, so this handles subsequent changes).
  if (focusedRow !== prevFocusedRow) {
    setPrevFocusedRow(focusedRow)

    if (focusedRow) {
      setActiveIndex(items.findIndex(item => item[itemIdKey] === focusedRow))
    } else {
      setActiveIndex(initialIndex)
    }
  }

  const itemsCount = items.length

  const [prevItemsCount, setPrevItemsCount] = useState(itemsCount)

  // Clamp the active index when the items count changes — during render.
  if (itemsCount !== prevItemsCount) {
    setPrevItemsCount(itemsCount)

    if (itemsCount === 0) {
      setActiveIndex(initialIndex)
    } else {
      setActiveIndex(prevIndex => {
        if (prevIndex >= itemsCount) {
          return itemsCount - 1
        }

        return prevIndex
      })
    }
  }

  function isIndexDisabled(index) {
    if (index < 0 || index >= itemsCount) {
      return true
    }

    return isValueFunctionType(isItemDisabled)
      ? Boolean(isItemDisabled(items[index], index))
      : false
  }

  function findNextEnabledIndex(startIndex, direction) {
    let nextIndex = startIndex

    while (nextIndex >= 0 && nextIndex < itemsCount) {
      if (!isIndexDisabled(nextIndex)) {
        return nextIndex
      }

      nextIndex += direction
    }

    return -1
  }

  function scrollItemIntoView(index) {
    if (scrollIntoView && containerRef?.current && index > -1) {
      const element = containerRef.current.querySelector(
        `[data-navigation-index="${index}"]`
      )

      element?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }

  function changeActiveIndex(nextIndex) {
    if (
      disabled ||
      itemsCount === 0 ||
      nextIndex < 0 ||
      nextIndex >= itemsCount
    ) {
      return
    }

    const enabledIndex = isIndexDisabled(nextIndex)
      ? findNextEnabledIndex(nextIndex, 1)
      : nextIndex

    if (enabledIndex > -1) {
      setActiveIndex(prevIndex => {
        if (prevIndex === enabledIndex) {
          return prevIndex
        }

        onActiveIndexChange?.(items[enabledIndex][itemIdKey])

        return enabledIndex
      })

      scrollItemIntoView(enabledIndex)
    }
  }

  function move(direction) {
    if (disabled || itemsCount === 0) {
      return
    }

    const currentIndex = activeIndexRef.current

    const startIndex =
      currentIndex === -1
        ? direction > 0
          ? 0
          : itemsCount - 1
        : currentIndex + direction

    const nextIndex = findNextEnabledIndex(startIndex, direction)

    if (nextIndex !== -1) {
      changeActiveIndex(nextIndex)
    }
  }

  function moveUp() {
    move(-1)
  }

  function moveDown() {
    move(1)
  }

  function handleKeyDown(event) {
    if (!disabled) {
      switch (event.key) {
        case EVENT_KEY_ARROW_UP:
          preventDefault(event)
          moveUp()
          break

        case EVENT_KEY_ARROW_DOWN:
          preventDefault(event)
          moveDown()
          break

        default:
          break
      }
    }
  }

  useEffect(() => {
    if (!disabled) {
      document.addEventListener(EVENT_NAME_KEY_DOWN, handleKeyDown)

      return () => {
        document.removeEventListener(EVENT_NAME_KEY_DOWN, handleKeyDown)
      }
    }
  }, [containerRef, disabled, handleKeyDown])

  function getItemProps(index) {
    return {
      'data-navigation-index': index,
    }
  }

  return {
    activeIndex,
    activeItem: activeIndex >= 0 ? items[activeIndex] : null,
    getItemProps,
    setActiveIndex,
  }
}
